import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { 
  EmailAuthProvider, 
  reauthenticateWithCredential, 
  updatePassword 
} from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { currentUser } = useAuth();
  const toast = useToast();
  
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const toggleShowPassword = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    if (!currentUser || !currentUser.email) {
      toast({
        title: 'Error',
        description: 'You must be signed in to change your password',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Validation
    if (!formData.currentPassword) {
      setError('Please enter your current password');
      return;
    }

    if (!formData.newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword === formData.currentPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        formData.currentPassword
      );
      
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, formData.newPassword);

      toast({
        title: 'Password updated',
        description: 'Your password has been changed successfully',
        status: 'success',
        duration: 3000,
      });

      // Reset form and close
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      onClose();
    } catch (error: any) {
      console.error('Password change error:', error);
      
      if (error.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('Please sign out and sign in again before changing your password');
      } else if (error.code === 'auth/weak-password') {
        setError('New password is too weak. Please use a stronger password');
      } else {
        setError(error.message || 'Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setError('');
    onClose();
  };

  const inputStyles = {
    bg: '#1C1C1E',
    border: 'none',
    borderRadius: 'xl',
    color: 'white',
    py: 6,
    px: 4,
    fontSize: 'md',
    _placeholder: { color: 'gray.500' },
    _focus: { 
      boxShadow: '0 0 0 1px rgba(255, 107, 53, 0.5)',
      bg: '#252528',
    },
    _hover: { bg: '#252528' },
  };

  const labelStyles = {
    color: 'gray.400',
    fontSize: 'xs',
    textTransform: 'lowercase' as const,
    mb: 1,
  };

  // Check if user signed in with Google (no password)
  const isGoogleUser = currentUser?.providerData?.some(
    provider => provider.providerId === 'google.com'
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0} borderRadius={0}>
        {/* Header */}
        <HStack 
          justify="space-between" 
          px={4} 
          py={4}
          borderBottom="1px solid"
          borderColor="gray.800"
        >
          <Text
            color="gray.400"
            fontSize="md"
            cursor="pointer"
            onClick={handleClose}
            _hover={{ color: 'white' }}
          >
            Cancel
          </Text>
          <Text color="white" fontSize="md" fontWeight="semibold">
            Change Password
          </Text>
          <Text
            color={isGoogleUser ? 'gray.600' : 'orange.400'}
            fontSize="md"
            fontWeight="semibold"
            cursor={loading || isGoogleUser ? 'not-allowed' : 'pointer'}
            onClick={loading || isGoogleUser ? undefined : handleSave}
            opacity={loading ? 0.5 : 1}
            _hover={isGoogleUser ? {} : { color: 'orange.300' }}
          >
            Save
          </Text>
        </HStack>

        <ModalBody py={6} px={4} overflowY="auto">
          <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
            {isGoogleUser ? (
              <Alert status="info" bg="blue.900" borderRadius="xl">
                <AlertIcon color="blue.300" />
                <Text color="blue.200" fontSize="sm">
                  You signed in with Google. Password changes are managed through your Google account.
                </Text>
              </Alert>
            ) : (
              <>
                {error && (
                  <Alert status="error" bg="red.900" borderRadius="xl">
                    <AlertIcon color="red.300" />
                    <Text color="red.200" fontSize="sm">{error}</Text>
                  </Alert>
                )}

                {/* Security Notice */}
                <Box 
                  bg="orange.900" 
                  p={4} 
                  borderRadius="xl"
                  borderLeft="4px solid"
                  borderColor="orange.400"
                >
                  <Text color="orange.200" fontSize="sm">
                     For security, you'll need to enter your current password to make changes.

                  </Text>
                </Box>

                {/* Current Password */}
                <Box>
                  <Text {...labelStyles}>Current password</Text>
                  <InputGroup>
                    <Input
                      {...inputStyles}
                      type={showPasswords.current ? 'text' : 'password'}
                      value={formData.currentPassword}
                      onChange={(e) => handleChange('currentPassword', e.target.value)}
                      placeholder="Enter current password"
                      pr="50px"
                    />
                    <InputRightElement h="100%" pr={2}>
                      <Text
                        color="gray.500"
                        fontSize="sm"
                        cursor="pointer"
                        onClick={() => toggleShowPassword('current')}
                        _hover={{ color: 'gray.300' }}
                      >
                        {showPasswords.current ? 'Hide' : 'Show'}
                      </Text>
                    </InputRightElement>
                  </InputGroup>
                </Box>

                {/* New Password */}
                <Box>
                  <Text {...labelStyles}>New password</Text>
                  <InputGroup>
                    <Input
                      {...inputStyles}
                      type={showPasswords.new ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={(e) => handleChange('newPassword', e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      pr="50px"
                    />
                    <InputRightElement h="100%" pr={2}>
                      <Text
                        color="gray.500"
                        fontSize="sm"
                        cursor="pointer"
                        onClick={() => toggleShowPassword('new')}
                        _hover={{ color: 'gray.300' }}
                      >
                        {showPasswords.new ? 'Hide' : 'Show'}
                      </Text>
                    </InputRightElement>
                  </InputGroup>
                </Box>

                {/* Confirm New Password */}
                <Box>
                  <Text {...labelStyles}>Confirm new password</Text>
                  <InputGroup>
                    <Input
                      {...inputStyles}
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      placeholder="Re-enter new password"
                      pr="50px"
                    />
                    <InputRightElement h="100%" pr={2}>
                      <Text
                        color="gray.500"
                        fontSize="sm"
                        cursor="pointer"
                        onClick={() => toggleShowPassword('confirm')}
                        _hover={{ color: 'gray.300' }}
                      >
                        {showPasswords.confirm ? 'Hide' : 'Show'}
                      </Text>
                    </InputRightElement>
                  </InputGroup>
                </Box>

                {/* Password Requirements */}
                <Box bg="#1C1C1E" p={4} borderRadius="xl">
                  <Text color="gray.400" fontSize="sm" fontWeight="semibold" mb={2}>
                    Password requirements:
                  </Text>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Text 
                        color={formData.newPassword.length >= 6 ? 'green.400' : 'gray.500'} 
                        fontSize="sm"
                      >
                        {formData.newPassword.length >= 6 ? '' : '○'} At least 6 characters

                      </Text>
                    </HStack>
                    <HStack>
                      <Text 
                        color={formData.newPassword && formData.newPassword === formData.confirmPassword ? 'green.400' : 'gray.500'} 
                        fontSize="sm"
                      >
                        {formData.newPassword && formData.newPassword === formData.confirmPassword ? '' : '○'} Passwords match

                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </>
            )}

            {/* Spacer for safe area */}
            <Box h="80px" />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ChangePasswordModal;